import { defineStore } from 'pinia'
import { CardType } from '~/static/enums/card-type'
import { useApi } from '~/composables/FetchApi'
import { convertUrlToFile } from '~/utils/Helpers'
import { EditorObject } from '~/utils/editor/object.class'
import { ObjectType } from '~/static/enums/object-type'
import { ImageObject } from '~/utils/editor/objects/image.class'
import { EditorData } from '~/static/enums/editor-state'
import { GeneratedImageType } from '~/static/enums/generated-image-type'

interface EditorStore {
  id: number | null
  type: CardType
  data: EditorData | null
  isSaved: boolean
  isSavingError: boolean
  name: string
  nameForChanging: string
  nameError: string
  generatedImages: Partial<Record<GeneratedImageType, string[]>>
}

interface SavingResponse {
  id: number
}

interface CardResponse {
  card: {
    id: number
    type: CardType
    name: string
    data: string
  }
  generated_urls: Partial<Record<number, string[]>>
}

export const useEditorStore = defineStore('editor', {
  state: (): EditorStore => {
    return {
      id: null,
      type: CardType.ProductCard,
      data: null,
      isSaved: false,
      isSavingError: false,
      name: 'Без названия',
      nameForChanging: 'Без названия',
      nameError: '',
      generatedImages: {},
    }
  },
  actions: {
    async loadData(id: number): Promise<EditorData> {
      const { response, error } = await useApi<CardResponse>('card.view', { id })
      if (error) {
        // eslint-disable-next-line no-console
        console.log(error.error_code, error.error_msg)
      } else if (response) {
        this.id = response.card.id
        this.name = response.card.name
        this.nameForChanging = response.card.name
        this.type = response.card.type
        const mapped: Partial<Record<GeneratedImageType, string[]>> = {}
        for (const [ key, urls ] of Object.entries(response.generated_urls)) {
          mapped[Number(key) as GeneratedImageType] = urls
        }
        this.generatedImages = mapped
        const data: EditorData = JSON.parse(response.card.data)
        this.data = data
        this.isSaved = true
        return Promise.resolve(data)
      }

      return Promise.reject()
    },
    async save(preview: string, data: EditorData) {
      const response = await this.saveCard(preview, data, this.id)
      if (response) {
        this.id = response.id
        this.isSaved = true
        this.isSavingError = false
      }
    },
    async delete(id?: number) {
      const { response, error } = await useApi<boolean>('card.delete', { id: id ?? this.id }, HttpMethod.POST)
      if (error) {
        // eslint-disable-next-line no-console
        console.log(error.error_code, error.error_msg)
      } else if (response) {
        this.isSaved = true
        useRouter().push('/')
      }
    },
    async changeName(): Promise<boolean> {
      if (this.nameForChanging.length === 0) {
        this.nameError = 'Необходимо заполнить поле'
        return false
      } else {
        this.nameError = ''
      }
      if (this.id === null) {
        this.name = this.nameForChanging
        return true
      }
      const { response, error } = await useApi<boolean>('card.saveName', {}, HttpMethod.POST, {
        id: this.id,
        name: this.nameForChanging,
      })
      if (error) {
        // eslint-disable-next-line no-console
        console.log(error.error_code, error.error_msg)
        return false
      } else if (response) {
        this.name = this.nameForChanging
      }
      return response
    },
    async addToTemplates(): Promise<boolean> {
      if (this.id === null || this.type !== CardType.ProductCard) {
        return false
      }
      const { response, error } = await useApi<boolean>('card.moveToTemplate', { id: this.id }, HttpMethod.POST)
      if (error) {
        // eslint-disable-next-line no-console
        console.log(error.error_code, error.error_msg)
        return false
      } else if (response) {
        this.type = CardType.Template
      }
      return response
    },
    async removeFromTemplates(): Promise<boolean> {
      if (this.id === null || this.type !== CardType.Template) {
        return false
      }
      const { response, error } = await useApi<boolean>('card.removeFromTemplate', { id: this.id }, HttpMethod.POST)
      if (error) {
        // eslint-disable-next-line no-console
        console.log(error.error_code, error.error_msg)
        return false
      } else if (response) {
        this.type = CardType.ProductCard
      }
      return response
    },
    async saveCard(
      preview: string,
      data: EditorData,
      id: number | null = null,
      name: string | null = null,
    ) {
      const formData = new FormData()
      if (id !== null) {
        formData.append('id', id.toString())
      }
      if (name !== null) {
        formData.append('name', name)
      } else {
        formData.append('name', this.name)
      }
      formData.append('preview', await convertUrlToFile(preview))
      formData.append('data', JSON.stringify(data))
      const imageUrls: string[] = []
      for (const objectJsonString of data.objects) {
        const object: EditorObject = JSON.parse(objectJsonString)
        if (object.type === ObjectType.Image) {
          const image = (object as ImageObject)
          if (image.imageUrl) {
            imageUrls.push(image.imageUrl)
          }
          if (image.imageUrl !== image.originalUrl && image.originalUrl) {
            imageUrls.push(image.originalUrl)
          }
        }
      }
      for (const imageUrl of imageUrls) {
        formData.append('imageUrls[]', imageUrl)
      }
      for (const [ type, urls ] of Object.entries(this.generatedImages)) {
        if (urls) {
          for (const url of urls) {
            formData.append(`generatedUrls[${type}][]`, url)
          }
        }
      }

      try {
        const { response, error } = await useApi<SavingResponse>('card.save', {}, HttpMethod.POST, formData)

        if (error) {
          // eslint-disable-next-line no-console
          console.log(error.error_code, error.error_msg)
          this.isSavingError = true
        }
        return response
      } catch(e) {
        console.log(e)
        if (id !== null) {
          formData.delete('id')
          const { response, error } = await useApi<SavingResponse>('card.save', {}, HttpMethod.POST, formData)
          if (error) {
            // eslint-disable-next-line no-console
            console.log(error.error_code, error.error_msg)
            this.isSavingError = true
          }
          return response
        }
      }
    },
    async cloneCard(preview: string, data: EditorData) {
      const response = await this.saveCard(preview, data, null, 'Без названия')
      if (response) {
        return response.id
      }
      return null
    },
  },
})
